/**
 * Sales goals on phone calls (Lj, Oct 11: "'call 5 customers/leads this week' could be a goal"; "link to customers is
 * important, but also leads"). Two counts over the goal's window, read from `calls` (shared/calls.js — matched calls
 * to customers and leads, and a per-day count of the rest):
 *   calls:made        outgoing calls to customers and leads
 *   calls:customers   different customers / leads called (outgoing)
 * Both count leads and customers; the breakdown rows and the basis say which each person is (calls.js personKind —
 * the Bids stage the books mirror on opsCustomers, a repeat customer with work stays a Customer). Incoming and missed calls, and calls to
 * numbers that aren't a customer, are said in the notes — never counted. deptgoals.js spreads CALL_GOAL_METRICS into
 * its menu and hands these keys to CALL_COUNTS. Pure — no I/O.
 */
import { callsByCustomer, callsFor, otherCalls, formatCallMinutes, leadStatusKnown, customersWithWork } from './calls.js';
import { localDateStr } from './time.js';

export const CALL_GOAL_METRICS = Object.freeze({
  'calls:made': { label: 'Calls made', unit: '', kind: 'count', direction: 'up', hint: 'outgoing calls to customers and leads — matched by number from the phone’s call log (Android, Phone setup → Call log access)' },
  'calls:customers': { label: 'Customers called', unit: '', kind: 'count', direction: 'up', hint: 'different customers and leads you called (outgoing)' },
});

/** Sales' call goals as presets: 10 calls a week, 5 different people a week. */
export const CALL_PRESETS = Object.freeze([
  { key: 'calls:made', period: 'week', target: 10 },
  { key: 'calls:customers', period: 'week', target: 5 },
]);

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const KIND = { lead: 'Lead', customer: 'Customer' };

/** Everything both counts read for a window → { people, out, notes }. */
function callWindow(w, data = {}) {
  const calls = Array.isArray(data.calls) ? data.calls : [];
  const customers = data.customers || [];
  const span = { from: w.from, to: w.to };
  const withWork = customersWithWork(data.projects, data.jobs);   // a repeat customer's new enquiry is still a Customer
  const people = callsByCustomer(calls, customers, { ...span, withWork });
  const out = callsFor(calls, { ...span, direction: 'out' });
  const other = otherCalls(calls, span);
  const incoming = people.reduce((a, p) => a + p.in, 0);
  const missed = people.reduce((a, p) => a + p.missed, 0);
  const called = people.filter((p) => p.out);
  const leads = called.filter((p) => p.kind === 'lead').length;
  const known = leadStatusKnown(customers);
  const notes = [];
  notes.push(`${plural(out.length, 'matched call')} out · ${plural(other.out, 'other call')} out to numbers that aren’t a customer or lead (counted, never kept)`);
  if (incoming || missed) notes.push(`${[incoming ? `${incoming} incoming` : null, missed ? `${missed} missed` : null].filter(Boolean).join(' and ')} with customers / leads — not counted`);
  if (called.length) notes.push(known ? `${plural(leads, 'lead')} and ${plural(called.length - leads, 'customer')} called (lead = Bids stage new → quoted, or lost; customer = won, no stage, or already has a job)` : 'leads count the same as customers — lead status isn’t on the device yet (needs the books update), so everyone reads as Customer');
  if (!calls.length) notes.push('no calls yet — the phone reads its call log from the next app build (Phone setup → Call log access)');
  return { people, out, notes, leads, customers: called.length - leads, known };
}

/** "2 leads, 3 customers" — the split by label (only once lead status is on the device). */
const split = (x) => (x.known && x.leads + x.customers ? ` — ${plural(x.leads, 'lead')}, ${plural(x.customers, 'customer')}` : '');

const rowsOf = (people) => people.filter((p) => p.out).map((p) => ({
  customerId: p.customerId,
  title: `${p.name} · ${KIND[p.kind]} · ${plural(p.out, 'call')} · ${formatCallMinutes(p.outSeconds)}`,
  date: p.lastOut ? localDateStr(p.lastOut) : null,   // the last call out (what the goal counts)
}));

/** calls:made — outgoing calls to customers and leads in the window. */
export function callsMade(dept, w, data = {}) {
  const x = callWindow(w, data);
  const n = x.people.filter((p) => p.out).length;
  return { value: x.out.length, rows: rowsOf(x.people), basis: `${plural(x.out.length, 'outgoing call')} to ${plural(n, 'customer / lead', 'customers / leads')}${split(x)}, matched by phone number on the phone`, notes: x.notes };
}

/** calls:customers — different customers / leads called (outgoing) in the window. */
export function customersCalled(dept, w, data = {}) {
  const x = callWindow(w, data);
  const rows = rowsOf(x.people);
  return { value: rows.length, rows, basis: `${plural(rows.length, 'customer / lead', 'customers / leads')} called (outgoing)${split(x)}, ${plural(x.out.length, 'call')} between them`, notes: x.notes };
}

export const CALL_COUNTS = Object.freeze({ 'calls:made': callsMade, 'calls:customers': customersCalled });
